import { supabase } from "@/integrations/supabase/client";
import type { AppRole } from "@/lib/auth";

export type Audience = "all" | "customers" | "partners" | "admins";

export const AUDIENCE_LABELS: Record<Audience, string> = {
  all: "All users",
  customers: "Customers",
  partners: "Partners",
  admins: "Admins",
};

export interface Announcement {
  id: string;
  title: string;
  message: string;
  audience: Audience;
  start_date: string;
  end_date: string | null;
  active: boolean;
  created_at: string;
}

export interface MaintenanceConfig {
  enabled: boolean;
  title: string;
  message: string;
  /** Optional ISO timestamp for when maintenance is expected to end. */
  scheduled_end?: string | null;
}

export const MAINTENANCE_DEFAULTS: MaintenanceConfig = {
  enabled: false,
  title: "We'll be right back",
  message: "Landoo is undergoing scheduled maintenance. Please check back shortly.",
  scheduled_end: null,
};

export async function fetchMaintenance(): Promise<MaintenanceConfig> {
  const { data, error } = await supabase
    .from("platform_settings")
    .select("value")
    .eq("key", "maintenance")
    .maybeSingle();
  if (error) throw error;
  return { ...MAINTENANCE_DEFAULTS, ...((data?.value as Partial<MaintenanceConfig>) ?? {}) };
}

export async function saveMaintenance(cfg: MaintenanceConfig): Promise<void> {
  const { error } = await supabase
    .from("platform_settings")
    .upsert(
      { key: "maintenance", value: cfg as never, description: "Maintenance mode configuration" },
      { onConflict: "key" },
    );
  if (error) throw error;
}

export function audiencesForRoles(roles: AppRole[]): Audience[] {
  const list: Audience[] = ["all"];
  if (roles.includes("customer") || roles.length === 0) list.push("customers");
  if (roles.includes("partner")) list.push("partners");
  if (roles.includes("admin") || roles.includes("super_admin")) list.push("admins");
  return list;
}

export async function fetchLiveAnnouncements(roles: AppRole[]): Promise<Announcement[]> {
  const { data, error } = await supabase
    .from("announcements")
    .select("id,title,message,audience,start_date,end_date,active,created_at")
    .eq("active", true)
    .in("audience", audiencesForRoles(roles))
    .order("created_at", { ascending: false });
  if (error) throw error;
  const now = Date.now();
  return ((data ?? []) as Announcement[]).filter(
    (a) =>
      new Date(a.start_date).getTime() <= now &&
      (!a.end_date || new Date(a.end_date).getTime() > now),
  );
}

export async function fetchDismissedIds(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("announcement_dismissals")
    .select("announcement_id")
    .eq("user_id", userId);
  if (error) throw error;
  return ((data ?? []) as { announcement_id: string }[]).map((d) => d.announcement_id);
}

export async function dismissAnnouncement(announcementId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from("announcement_dismissals")
    .upsert(
      { announcement_id: announcementId, user_id: userId },
      { onConflict: "announcement_id,user_id" },
    );
  if (error) throw error;
}

/* ---------- Super Admin management ---------- */

export async function fetchAllAnnouncements(): Promise<Announcement[]> {
  const { data, error } = await supabase
    .from("announcements")
    .select("id,title,message,audience,start_date,end_date,active,created_at")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Announcement[];
}

export interface AnnouncementInput {
  title: string;
  message: string;
  audience: Audience;
  start_date: string;
  end_date: string | null;
  active: boolean;
}

export async function createAnnouncement(input: AnnouncementInput, userId: string): Promise<void> {
  const { error } = await supabase.from("announcements").insert({ ...input, created_by: userId });
  if (error) throw error;
}

export async function updateAnnouncement(
  id: string,
  patch: Partial<AnnouncementInput>,
): Promise<void> {
  const { error } = await supabase.from("announcements").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteAnnouncement(id: string): Promise<void> {
  const { error } = await supabase.from("announcements").delete().eq("id", id);
  if (error) throw error;
}

/* ---------- Maintenance audit log ---------- */

export interface MaintenanceAuditEntry {
  id: string;
  actor_id: string | null;
  action: string;
  metadata: {
    enabled?: boolean;
    scheduled_end?: string | null;
    actor_email?: string | null;
  } | null;
  created_at: string;
}

export async function logMaintenanceToggle(
  userId: string,
  cfg: MaintenanceConfig,
  actorEmail?: string | null,
): Promise<void> {
  const { error } = await supabase.from("audit_logs").insert({
    actor_id: userId,
    action: cfg.enabled ? "maintenance_enabled" : "maintenance_disabled",
    entity_type: "platform_settings",
    metadata: {
      enabled: cfg.enabled,
      scheduled_end: cfg.scheduled_end ?? null,
      actor_email: actorEmail ?? null,
    } as never,
  });
  if (error) throw error;
}

export async function fetchMaintenanceAudit(): Promise<MaintenanceAuditEntry[]> {
  const { data, error } = await supabase
    .from("audit_logs")
    .select("id,actor_id,action,metadata,created_at")
    .in("action", ["maintenance_enabled", "maintenance_disabled"])
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as unknown as MaintenanceAuditEntry[];
}

/* ---------- Announcement edit history ---------- */

export interface AnnouncementHistoryEntry {
  id: string;
  announcement_id: string;
  changed_by: string | null;
  changes: Record<string, { from: unknown; to: unknown }>;
  created_at: string;
}

export async function fetchAnnouncementHistory(
  announcementId: string,
): Promise<AnnouncementHistoryEntry[]> {
  const { data, error } = await supabase
    .from("announcement_history")
    .select("id,announcement_id,changed_by,changes,created_at")
    .eq("announcement_id", announcementId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as AnnouncementHistoryEntry[];
}

export const FIELD_LABELS: Record<string, string> = {
  title: "Title",
  message: "Message",
  audience: "Audience",
  start_date: "Start date",
  end_date: "End date",
  active: "Status",
};

export function formatHistoryValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (field === "active") return value ? "Active" : "Inactive";
  if (field === "start_date" || field === "end_date")
    return new Date(String(value)).toLocaleString();
  if (field === "audience") return AUDIENCE_LABELS[value as Audience] ?? String(value);
  return String(value);
}

/** Announcements that have not started yet — used by the admin preview page. */
export async function fetchUpcomingAnnouncements(): Promise<Announcement[]> {
  const { data, error } = await supabase
    .from("announcements")
    .select("id,title,message,audience,start_date,end_date,active,created_at")
    .gt("start_date", new Date().toISOString())
    .order("start_date", { ascending: true });
  if (error) throw error;
  return (data ?? []) as Announcement[];
}

export async function undismissAnnouncement(announcementId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from("announcement_dismissals")
    .delete()
    .eq("announcement_id", announcementId)
    .eq("user_id", userId);
  if (error) throw error;
}
